import { useEffect, useRef, useState } from 'react';
import type { Article } from '@/types/article';

interface SharePanelProps {
  article: Article;
}

export default function SharePanel({ article }: SharePanelProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = encodeURIComponent(`${article.title} - 91黑料网`);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socials = [
    {
      label: '微信',
      icon: 'ri-wechat-line',
      color: 'text-primary-600',
      onClick: copyLink,
    },
    {
      label: '微博',
      icon: 'ri-weibo-line',
      color: 'text-accent-500',
      onClick: () => window.open(
        `https://service.weibo.com/share/share.php?url=${encodeURIComponent(pageUrl)}&title=${shareText}`,
        '_blank',
      ),
    },
    {
      label: 'QQ',
      icon: 'ri-qq-line',
      color: 'text-secondary-600',
      onClick: () => window.open(
        `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(pageUrl)}&title=${shareText}`,
        '_blank',
      ),
    },
    {
      label: 'X',
      icon: 'ri-twitter-x-line',
      color: 'text-foreground-900',
      onClick: () => window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${shareText}`,
        '_blank',
      ),
    },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
      >
        <i className="ri-share-line"></i>
        分享
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-40 w-56 animate-fade-in rounded-lg border border-background-200 bg-background-50 p-3 shadow-lg">
          <p className="mb-2 text-xs font-semibold text-foreground-500">分享到</p>
          <div className="grid grid-cols-4 gap-2">
            {socials.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={s.onClick}
                className="flex cursor-pointer flex-col items-center gap-1 rounded-md py-2 transition-colors hover:bg-background-100"
              >
                <i className={`${s.icon} text-xl ${s.color}`}></i>
                <span className="text-[11px] text-foreground-600">{s.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={copyLink}
            className="mt-2 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md bg-background-100 py-2 text-xs font-medium text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
          >
            <i className={copied ? 'ri-check-line text-primary-600' : 'ri-link'}></i>
            {copied ? '链接已复制' : '复制页面链接'}
          </button>
        </div>
      )}
    </div>
  );
}